import { TAGS, api } from "store/api"
import { INewBalance } from "store/apis/balanceAPI/balance.types"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { IResPag } from "utils/types"
import { getQueryParams } from "utils/getQueryParams"
import { bossEndpoints } from "../bossAPI/boss.api"
import { bossClansAPI } from "../bossClansAPI/bossClans"
import { IUpgradeBuilding } from "../clanAltarAPI/types"
import { clanMissionsApi } from "../clanMissionsAPI/clan-missions.api"
import {
  IClan,
  IFilters,
  IMember,
  IMemberFilters,
  IClanAvatar,
  ICreateClanDto,
  IUserClan,
  IChangeRoleDto,
  IExcludeMemberDto,
  IEnterClanDto,
  IClanBuildingInstance,
  IClanRequest,
  IRequestsFilters,
  IBuildingNextUpgradeDto,
  IGetUpgradeDto,
  CLAN_BUILDING_TYPE,
  IClanBuilding,
  IUpdateClanDto,
  IBuildingDto,
  IDonateDto,
  IClanTransaction,
  IGetTransactionsDto,
  ILeaderBoardFilters,
  IGetTaxesDto,
  IClanTax,
  ITaxCurrency,
  IActivityLogsDto,
  IActivityLog,
  IClanShort,
  IClanTransferDto
} from "./types"

export const clansAPI = api.enhanceEndpoints({
  addTagTypes: [
    TAGS.CLANS,
    TAGS.USER,
    TAGS.CLAN_ALTAR,
    TAGS.CLAN_MISSIONS,
    TAGS.DARK_CLAN_ALTAR
  ]
})

export const clansEndpoints = clansAPI.injectEndpoints({
  endpoints: (builder) => ({
    getClans: builder.query<{ items: IClan[]; total: number }, IFilters>({
      query: ({ search, page, limit, sortKey }) => {
        const query = getQueryParams({
          search,
          page,
          limit,
          sortKey
        })

        return {
          url: `/clan/?${query}`,
          method: "GET"
        }
      },
      extraOptions: { maxRetries: 2 },
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ _id }) => ({
                type: TAGS.CLANS as const,
                id: _id
              })),
              { type: TAGS.CLANS, id: "CLANS" }
            ]
          : [{ type: TAGS.CLANS, id: "CLANS" }]
    }),
    getClanById: builder.query<IClan, string>({
      query: (clanId) => ({
        url: `/clan/${clanId}`,
        method: "GET"
      })
    }),
    getClanShortByUserId: builder.query<IClanShort, string>({
      query: (userId) => ({
        url: `/clan/short-clan-info/${userId}`,
        method: "GET"
      }),
      providesTags: (_, __, userId) => [
        { type: TAGS.CLANS, id: "CLAN_SHORT" },
        { type: TAGS.CLANS, id: `CLAN_SHORT_${userId}` }
      ]
    }),
    getUserClan: builder.query<IUserClan | null, void>({
      query: () => ({
        url: "/clan/members-role",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.CLANS, id: "USER_CLAN" }]
    }),
    getClanPosition: builder.query<number, ILeaderBoardFilters>({
      query: ({ clanId, sortKey }) => {
        const query = getQueryParams({
          sortKey
        })

        return {
          url: `/clan/${clanId}/position/?${query}`,
          method: "GET"
        }
      },
      providesTags: [{ type: TAGS.CLANS, id: "CLAN_POSITION" }]
    }),
    getMembers: builder.query<
      { items: IMember[]; total: number },
      IMemberFilters
    >({
      query: ({ clanId, page = 1, limit = 50 }) => {
        const query = getQueryParams({
          page,
          limit
        })

        return {
          url: `/clan/${clanId}/members?${query}`,
          method: "GET"
        }
      },
      providesTags: () => [{ type: TAGS.CLANS, id: "MEMBERS" }]
    }),
    getDefaultAvatars: builder.query<
      { total: number; items: IClanAvatar[] },
      void
    >({
      query: () => ({
        url: "/clan/avatars",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.CLANS, id: "AVATARS" }]
    }),
    enterClan: builder.mutation<void, IEnterClanDto>({
      query: ({ clanId, message }) => ({
        url: `/clan/${clanId}/enter`,
        method: "POST",
        data: { message }
      }),
      invalidatesTags: [
        { type: TAGS.CLANS, id: "MEMBERS" },
        { type: TAGS.CLANS, id: "USER_CLAN" }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            bossClansAPI.util.invalidateTags([
              { type: TAGS.BOSS_CLANS, id: "BUILDINGS" }
            ])
          )
          dispatch(
            bossEndpoints.util.invalidateTags([
              { type: TAGS.BOSS, id: "STATS" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    getUserRequests: builder.query<
      {
        items: IClanRequest[]
        total: number
      },
      IRequestsFilters
    >({
      query: ({ clanId, page, limit }) => {
        const query = getQueryParams({
          page,
          limit,
          clanId
        })

        return {
          url: `/clan/requests?${query}`,
          method: "GET"
        }
      },
      providesTags: [{ type: TAGS.CLANS, id: "USER_APPLICATIONS" }]
    }),
    createClan: builder.mutation<IClan, ICreateClanDto>({
      query: (createData) => {
        const formData = new FormData()

        formData.append("name", createData.name)
        formData.append("abbreviation", createData.abbreviation)
        formData.append("avatarId", createData.avatarId || "")
        formData.append("closed", createData.closed.toString())

        if (createData.description) {
          formData.append("description", createData.description)
        }

        if (createData.minPowerToEnter) {
          formData.append(
            "minPowerToEnter",
            String(Number(createData.minPowerToEnter))
          )
        }

        if (createData.avatar) {
          formData.append("file", createData.avatar)
        }

        return {
          url: "/clan",
          method: "POST",
          data: formData
        }
      },
      invalidatesTags: [
        { type: TAGS.CLANS, id: "USER_CLAN" },
        { type: TAGS.USER, id: "user" },
        { type: TAGS.CLANS, id: "CLANS" }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            bossClansAPI.util.invalidateTags([
              { type: TAGS.BOSS_CLANS, id: "BUILDINGS" }
            ])
          )
          dispatch(
            bossEndpoints.util.invalidateTags([
              { type: TAGS.BOSS, id: "STATS" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    updateClan: builder.mutation<IClan, IUpdateClanDto>({
      query: (updateData) => {
        const formData = new FormData()

        formData.append("clanId", updateData.clanId)

        if (updateData.name) {
          formData.append("name", updateData.name)
        }

        if (updateData.abbreviation) {
          formData.append("abbreviation", updateData.abbreviation)
        }

        if (updateData.closed !== undefined) {
          formData.append("closed", updateData.closed.toString())
        }

        if (updateData.description) {
          formData.append("description", updateData.description)
        }

        if (updateData.minPowerToEnter) {
          formData.append(
            "minPowerToEnter",
            String(Number(updateData.minPowerToEnter))
          )
        }

        if (updateData.avatarId) {
          formData.append("avatarId", updateData.avatarId)
        }

        if (updateData.file) {
          formData.append("file", updateData.file)
        }

        return {
          url: "/clan",
          method: "PATCH",
          data: formData
        }
      }
    }),
    leaveClan: builder.mutation<void, string>({
      query: (clanId) => ({
        url: `/clan/${clanId}/leave`,
        method: "POST"
      }),
      invalidatesTags: [{ type: TAGS.CLANS }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            bossClansAPI.util.invalidateTags([
              { type: TAGS.BOSS_CLANS, id: "BUILDINGS" }
            ])
          )
          dispatch(
            bossEndpoints.util.invalidateTags([
              { type: TAGS.BOSS, id: "STATS" },
              { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_CLANS" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    changeRole: builder.mutation<void, IChangeRoleDto>({
      query: (changeRoleDto) => ({
        url: "/clan/members-role",
        method: "PATCH",
        data: changeRoleDto
      }),
      invalidatesTags: [{ type: TAGS.CLANS, id: "MEMBERS" }]
    }),
    transferClan: builder.mutation<void, IClanTransferDto>({
      query: ({ clanId, candidateId }) => ({
        url: `/clan/${clanId}/transfer`,
        method: "PATCH",
        data: {
          candidateId
        }
      }),
      invalidatesTags: [
        { type: TAGS.CLANS, id: "USER_CLAN" },
        { type: TAGS.CLANS, id: "MEMBERS" }
      ]
    }),
    kickMember: builder.mutation<void, IExcludeMemberDto>({
      query: (excludeDto) => ({
        url: "/clan/exclude-member",
        method: "PATCH",
        data: excludeDto
      }),
      invalidatesTags: [
        { type: TAGS.CLANS, id: "MEMBERS" },
        { type: TAGS.CLANS, id: "USER_CLAN" }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            bossEndpoints.util.invalidateTags([
              { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_CLANS" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    deleteClan: builder.mutation<void, { clanId: string }>({
      query: ({ clanId }) => ({
        url: `/clan/${clanId}/delete-clan`,
        method: "DELETE"
      }),
      invalidatesTags: [
        { type: TAGS.CLANS },
        { type: TAGS.USER, id: "user" },
        { type: TAGS.CLANS, id: "USER_CLAN" },
        { type: TAGS.CLAN_ALTAR }
      ]
    }),
    donateToBank: builder.mutation<{ newBalance: INewBalance }, IDonateDto>({
      query: (dto) => ({
        url: "/clan/donate",
        method: "PATCH",
        data: dto
      }),
      invalidatesTags: [
        { type: TAGS.CLANS, id: "USER_CLAN" },
        { type: TAGS.CLANS, id: "TRANSACTIONS" }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            balanceEndpoints.util.updateQueryData(
              "getBalance",
              undefined,
              () => data.newBalance
            )
          )

          dispatch(
            clanMissionsApi.util.invalidateTags([
              { type: TAGS.CLAN_MISSIONS, id: "MY_PROGRESS" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    getClanBuildings: builder.query<
      {
        items: IClanBuildingInstance[]
        total: number
      },
      string
    >({
      query: (clanId) => ({
        url: `/clan/${clanId}/buildings`,
        method: "GET"
      }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map(({ type }) => ({
                type: TAGS.CLANS as const,
                id: `BUILDINGS-${type}`
              })),
              { type: TAGS.CLANS, id: "BUILDINGS" }
            ]
          : [{ type: TAGS.CLANS, id: "BUILDINGS" }]
    }),
    getBuildingByType: builder.query<
      IClanBuilding,
      { type: CLAN_BUILDING_TYPE }
    >({
      query: ({ type }) => ({
        url: `/clan/buildings/${type}`,
        method: "GET"
      }),
      providesTags: (res) =>
        res
          ? [
              { type: TAGS.CLANS, id: "BUILDING-INSTANCE" },
              { type: TAGS.CLANS, id: `BUILDINGS-${res.type}` }
            ]
          : [{ type: TAGS.CLANS, id: "BUILDING-INSTANCE" }]
    }),
    getClanBuildingInstance: builder.query<
      IClanBuildingInstance,
      { clanId: string; type: CLAN_BUILDING_TYPE }
    >({
      query: (dto) => ({
        url: `/clan/${dto.clanId}/buildings/${dto.type}`,
        method: "GET"
      }),
      providesTags: (res) => {
        const tags: { type: TAGS.USER | TAGS.CLANS; id: string }[] = [
          { type: TAGS.CLANS, id: "CLAN-BUILDING-INSTANCE" }
        ]

        return res
          ? [
              ...tags,
              {
                type: TAGS.CLANS,
                id: `CLAN-${res.clanId}-BUILDINGS-${res.type}`
              }
            ]
          : [...tags]
      }
    }),
    buildBuilding: builder.mutation<
      { level: number; type: CLAN_BUILDING_TYPE },
      IBuildingDto
    >({
      query: (dto) => ({
        url: `/clan/${dto.clanId}/buildings/${dto.type}`,
        method: "PATCH"
      }),
      invalidatesTags: () => [
        { type: TAGS.CLANS, id: "BUILDINGS" },
        { type: TAGS.CLANS, id: "USER_CLAN" },
        { type: TAGS.CLANS, id: "CLAN-BUILDING-INSTANCE" },
        { type: TAGS.CLANS, id: "BUILDING-UPGRADES" }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            clansAPI.util.invalidateTags([
              { type: TAGS.CLANS, id: "ACTIVITIES" }
            ])
          )
          dispatch(
            bossClansAPI.util.invalidateTags([
              { type: TAGS.BOSS_CLANS, id: "BUILDINGS" }
            ])
          )
          dispatch(
            bossEndpoints.util.invalidateTags([
              { type: TAGS.BOSS, id: "STATS" }
            ])
          )
        } catch {
          // catch
        }
      }
    }),
    upgradeBuilding: builder.mutation<
      { level: number; type: CLAN_BUILDING_TYPE; price: IUpgradeBuilding[] },
      IBuildingDto
    >({
      query: ({ clanId, type }) => ({
        url: `/clan/${clanId}/buildings/${type}`,
        method: "PATCH"
      }),
      invalidatesTags: (_, __, { clanId, type }) => {
        const tags: {
          type: TAGS.CLANS | TAGS.CLAN_ALTAR | TAGS.DARK_CLAN_ALTAR
          id: string
        }[] = [
          { type: TAGS.CLANS, id: `CLAN-${clanId}-BUILDINGS-${type}` },
          { type: TAGS.CLANS, id: "BUILDING-UPGRADES" }
        ]

        if (type == CLAN_BUILDING_TYPE.ALTAR) {
          tags.push({ type: TAGS.CLAN_ALTAR, id: "INSTANCE" })
          tags.push({ type: TAGS.CLAN_ALTAR, id: "BONUSES_USER" })
          tags.push({ type: TAGS.DARK_CLAN_ALTAR, id: "INSTANCE" })
          tags.push({ type: TAGS.DARK_CLAN_ALTAR, id: "DATA" })
          tags.push({ type: TAGS.DARK_CLAN_ALTAR, id: "BONUSES_USER" })
          tags.push({ type: TAGS.DARK_CLAN_ALTAR, id: "BONUSES_CLAN" })
        }

        return tags
      },
      async onQueryStarted({ clanId, type }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            clansEndpoints.util.updateQueryData(
              "getClanBuildings",
              clanId,
              (draft) => {
                const building = draft.items.find((b) => b.type === type)
                if (building) {
                  building.level = data.level
                }
              }
            )
          )

          dispatch(
            clansEndpoints.util.updateQueryData(
              "getUserClan",
              undefined,
              (draft) => {
                if (draft?.clan?.balance) {
                  data.price.forEach((p) => {
                    const currency =
                      p.currency as keyof typeof draft.clan.balance
                    if (draft.clan.balance[currency] !== undefined) {
                      draft.clan.balance[currency] -= p.value
                    }
                  })
                }
              }
            )
          )

          dispatch(
            clansAPI.util.invalidateTags([
              { type: TAGS.CLANS, id: "ACTIVITIES" }
            ])
          )
          dispatch(
            bossClansAPI.util.invalidateTags([
              { type: TAGS.BOSS_CLANS, id: "BUILDINGS" }
            ])
          )
          dispatch(
            bossEndpoints.util.invalidateTags([
              { type: TAGS.BOSS, id: "STATS" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    getClanRequests: builder.query<
      {
        items: IClanRequest[]
        total: number
      },
      IRequestsFilters
    >({
      query: ({ clanId, page, limit }) => {
        const query = getQueryParams({
          page,
          limit
        })

        return {
          url: `/clan/${clanId}/requests?${query}`,
          method: "GET"
        }
      },
      providesTags: [{ type: TAGS.CLANS, id: "APPLICATIONS" }]
    }),
    approveRequest: builder.mutation<boolean, string>({
      query: (requestId) => ({
        url: `/clan/requests/${requestId}/approve`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.CLANS, id: "APPLICATIONS" },
        { type: TAGS.CLANS, id: "USER_CLAN" }
      ]
    }),
    rejectRequest: builder.mutation<boolean, string>({
      query: (requestId) => ({
        url: `/clan/requests/${requestId}/reject`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.CLANS, id: "APPLICATIONS" },
        { type: TAGS.CLANS, id: "USER_CLAN" }
      ]
    }),

    getBuildingUpgrade: builder.query<IBuildingNextUpgradeDto, IGetUpgradeDto>({
      query: ({ type }) => ({
        url: `/clan/buildings/upgrades/${type}`,
        method: "GET"
      }),
      providesTags: [{ type: TAGS.CLANS, id: "BUILDING-UPGRADES" }]
    }),

    getClanTransactions: builder.query<
      { items: IClanTransaction[]; total: number },
      IGetTransactionsDto
    >({
      query: ({ clanId, userName, page, limit, startedAt, mode }) => {
        const query = getQueryParams({
          userName: userName?.trim() || undefined,
          page,
          limit,
          startedAt,
          mode
        })

        return {
          url: `/clan/${clanId}/donate-logs?${query}`,
          method: "GET"
        }
      },
      providesTags: (_, __, { mode, page, limit, startedAt, userName }) => {
        const parts = [
          mode || "any",
          page ?? 1,
          limit ?? "all",
          startedAt || "none",
          userName?.trim() || "all"
        ]

        return [
          { type: TAGS.CLANS, id: `TRANSACTIONS` },
          { type: TAGS.CLANS, id: `TRANSACTIONS_${parts.join("_")}` }
        ]
      }
    }),
    getClanTaxes: builder.query<
      { items: IClanTax[]; totalAvailable: ITaxCurrency },
      IGetTaxesDto
    >({
      query: ({ clanId, page, limit }) => {
        const query = getQueryParams({
          page,
          limit
        })

        return {
          url: `/clan/${clanId}/taxes?${query}`,
          method: "GET"
        }
      },
      providesTags: [{ type: TAGS.CLANS, id: "TAXES" }]
    }),
    claimClanTaxes: builder.mutation<ITaxCurrency, string>({
      query: (clanId) => ({
        url: `clan/${clanId}/taxes`,
        method: "PATCH"
      }),
      invalidatesTags: [{ type: TAGS.CLANS, id: "TAXES" }]
    }),
    getClanLogs: builder.query<IResPag<IActivityLog>, IActivityLogsDto>({
      query: ({ clanId, type, page, limit }) => {
        const query = getQueryParams({
          type,
          page,
          limit
        })

        return {
          url: `/clan/${clanId}/logs?${query}`,
          method: "GET"
        }
      },
      providesTags: (_, __, { type }) => [
        { type: TAGS.CLANS, id: `ACTIVITIES` },
        { type: TAGS.CLANS, id: `ACTIVITIES_${type}` }
      ]
    })
  })
})
